<template>
  <div v-if="!licenseStore.limits.canShare">
    <subscription-ordering-form
      :same-page="true"
    >
      <div class="message message-notice p-4">
        <lucide-icon name="info" class="message__icon" />
        {{ $t("messages.noShareLicense") }}
      </div>
    </subscription-ordering-form>
  </div>
  <div v-else class="shared-models-view">
    <h1 class="mt-0">{{ $t("labels.sharedModels") }}</h1>

    <div class="line_flex -between">
      <button
        class="workspacefolder-view__loading"
        :title="$t('actions.updateNow')"
        type="button"
        @click="loadModels"
      >
        <span class="workspacefolder-view__loadingstatus">
          <span v-if="state.loading">{{ $t("messages.loadingData") }}</span>
          <span v-else>
            {{ $t("labels.lastUpdatedXAgo", [lastUpdatedFormatted]) }}
          </span>
        </span>

        <lucide-icon
          v-show="!state.loading"
          name="refresh-cw"
          class="workspacefolder-view__reloadicon"
        />
      </button>

      <button
        class="button-icon pr-0"
        type="button"
        @click="state.listOptionsVisible = true"
      >
        <lucide-icon name="settings-2" />
      </button>
    </div>

    <error-page-content
      v-if="!state.loading && state.loadingError"
      :status="state.loadingError.status"
      :error-code="state.loadingError.code"
    />

    <models-grid v-else>
      <div></div>
      <!-- Make sure div is not first child... -->
      <p v-if="!state.loading && sharedModelsCount == 0" class="message message-info mt-4">
        {{ $t("texts.noSharedModels") }}
      </p>
      <div class="column gap-4 column--container">
        <!-- eslint-disable prettier/prettier -->
        <model-entry
          v-for="sharedModel in orderedNonArchivedSharedModels"
          :key="sharedModel.id"
          :model="sharedModel"
          :description-visible="modelDescriptionsVisible"
          @copy="modelToDuplicate = ($event as SharedModel)"
          @contextmenu="openContextMenu(sharedModel, $event)"
        ></model-entry>
        <!-- eslint-enable prettier/prettier -->
      </div>
    </models-grid>

    <drawer-block
      v-if="archivedSharedModels.length > 0"
      :title="$t('headings.archivedModels')"
      style="--padding-horizontal: 0"
      class="mt-4"
    >
      <div class="column gap-4 column--container">
        <!-- eslint-disable prettier/prettier -->
        <model-entry
          v-for="sharedModel in archivedSharedModels"
          :key="sharedModel.id"
          :model="sharedModel"
          :description-visible="modelDescriptionsVisible"
          @copy="modelToDuplicate = ($event as SharedModel)"
          @contextmenu="openContextMenu(sharedModel, $event)"
        ></model-entry>
        <!-- eslint-enable prettier/prettier -->
      </div>
    </drawer-block>

    <model-list-options
      v-model:order-by="orderModelsBy"
      v-model:show-descriptions="modelDescriptionsVisible"
      :visible="state.listOptionsVisible"
      @close="state.listOptionsVisible = false"
    />

    <model-duplicate-popup
      :visible="modelToDuplicate != null"
      :model="modelToDuplicate"
      @close="modelToDuplicate = undefined"
    />

    <context-menu
      v-if="contextMenuState.sharedModel"
      :x="contextMenuState.x"
      :y="contextMenuState.y"
      @close="contextMenuState.sharedModel = null"
    >
      <router-link
        class="context-menu__entry"
        :to="{
          name: 'shared-model-info',
          params: { shareId: contextMenuState.sharedModel.id }
        }"
      >
        <span>{{ $t("labels.info") }}</span>
      </router-link>
      <button
        v-if="contextMenuState.sharedModel.permissions.canFork"
        class="context-menu__entry"
        type="button"
        @click="modelToDuplicate = contextMenuState.sharedModel"
      >
        {{ $t("labels.duplicate") }}
      </button>
      <button
        v-if="contextMenuState.sharedModel.permissions.canDownload"
        type="button"
        class="context-menu__entry"
        @click="downloadModel(contextMenuState.sharedModel)"
      >
        {{ $t("labels.download") }}
      </button>
      <button
        v-if="!contextMenuState.sharedModel.archived"
        type="button"
        class="context-menu__entry"
        @click="archiveSharedModel(contextMenuState.sharedModel.id)"
      >
        {{ $t("actions.archive") }}
      </button>
      <button
        v-else
        type="button"
        class="context-menu__entry"
        @click="unArchiveSharedModel(contextMenuState.sharedModel.id)"
      >
        {{ $t("actions.restore") }}
      </button>
      <button
        type="button"
        class="context-menu__entry"
        @click="tryToDeleteSharedModel(contextMenuState.sharedModel.id)"
      >
        {{ $t("actions.delete") }}
      </button>
    </context-menu>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, reactive, watch } from "vue";
import { DateTime } from "luxon";
import { formatDateRelative } from "@/i18n";
import { useI18n } from "vue-i18n";

import { useSharedModels } from "@/api/sharedModels.store";
import { useUser } from "@/api/user.store";
import { useNow } from "@core/composables/useNow";

import LucideIcon from "@core/ui/LucideIcon.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import DrawerBlock from "@core/ui/DrawerBlock.vue";

import ModelsGrid from "@manager/workspaces/ModelsGrid.vue";
import ModelEntry from "@manager/workspacemodels/ModelEntry.vue";
import ModelListOptions from "@manager/workspacemodels/ModelListOptions.vue";
import ModelDuplicatePopup from "@manager/workspacemodels/ModelDuplicatePopup.vue";
import SubscriptionOrderingForm from "@manager/purchasing/SubscriptionOrderingForm.vue";

import ErrorPageContent from "@core/errors/ErrorPageContent.vue";

import type { SharedModel } from "@/api/SharedModel";

import { orderModels } from "@/api/modelFunctions";
import { downloadModelFile } from "@/api/SaveService";
import { showConfirm, showError } from "@core/ui/alert/alert";
import { useLicense } from "@/api/license.store";

const { user } = useUser();
const {
  sharedModelsCount,
  nonArchivedSharedModels,
  archivedSharedModels,
  loadSharedModels,
  getSharedModelData,
  deleteSharedModel,
  archiveSharedModel,
  unArchiveSharedModel
} = useSharedModels();

const licenseStore = useLicense();
const modelToDuplicate = ref<SharedModel>();
const orderModelsBy = ref(user.value?.preferredModelOrder ?? "modified");
const modelDescriptionsVisible = ref(user.value?.showModelDetails ?? true);

const now = useNow();
const { t } = useI18n();

const state = reactive({
  listOptionsVisible: false,
  lastUpdated: 0,
  loading: true,
  loadingError: null as null | Error & { status?: number; code?: string }
});

const orderedNonArchivedSharedModels = computed(() => {
  return orderModels(nonArchivedSharedModels.value, orderModelsBy.value);
});

const lastUpdatedFormatted = computed(() => {
  const date = new Date(state.lastUpdated);
  const dateTime = DateTime.fromJSDate(date);
  if (Math.abs(dateTime.diffNow("minutes").as("minutes")) < 1) {
    return t("messages.lessThenAMinuteAgo");
  }
  return formatDateRelative(date, { now: now.value });
});

// load the shared models and reload them everytime the user changes
watch(() => user.value?.id, (id, oldId) => {
  if (id != null && id !== oldId) {
    loadModels();
  }
}, { immediate: true });

async function loadModels() {
  state.loading = true;
  state.loadingError = null;
  try {
    await loadSharedModels().finally(() => {
      state.lastUpdated = Date.now();
      state.loading = false;
    });
  } catch (e) {
    state.loadingError = e as Error;
  }
}

async function downloadModel(sharedModel: SharedModel, name?: string) {
  const modelData = await getSharedModelData(sharedModel.id);

  if (modelData == null || modelData.data == null) {
    showError(t("errors.noModeldata"));
    return;
  }

  return downloadModelFile(sharedModel, modelData, {
    name: name || sharedModel.name,
    isSimulationGame: sharedModel.isSimulationGame
  });
}

async function tryToDeleteSharedModel(id: number) {
  if (
    await showConfirm(
      t("messages.confirmDeleteSharedModel"),
      t("actions.deleteModel"),
      t("labels.abort")
    )
  ) {
    deleteSharedModel(id);
  }
}

// ==============================
// CONTEXT MENU

const contextMenuState = reactive({
  sharedModel: null as null | SharedModel,
  x: 0,
  y: 0
});

async function openContextMenu(model: SharedModel, event: MouseEvent) {
  event.preventDefault();
  contextMenuState.sharedModel = model;
  contextMenuState.x = event.clientX;
  contextMenuState.y = event.clientY;
}
</script>

<style lang="scss">
.shared-models-view {
  width: 100%;
  max-width: 60rem;
  margin: 0 auto;
}
</style>
